import { Fragment } from 'react';
import { DatasetItem } from '../../../types/types';
import { TranslateFn, useTranslate } from '../../../hooks/context/useTranslate';
import {
  DEFAULT_LOGO_HEIGHT_MM,
  DEFAULT_LOGO_SRC,
  REPORT_GRID_COLUMNS,
} from '../constants';
import {
  ReportBlock,
  ReportAlignment,
  ReportDataSource,
  ReportRowsBySource,
  ReportTextStyle,
} from '../types';
import { snapGridRow } from '../utils/snapGridRow';
import { resolveTextStyle } from '../utils/resolveTextStyle';
import { computeAggregate } from '../utils/computeAggregate';
import { computeTaxBreakdown } from '../utils/computeTaxBreakdown';
import {
  describeActiveFilters,
  findFilterValue,
  formatFilterDescription,
} from '../utils/describeActiveFilters';
import { formatPrintNumber, formatPrintValue } from '../utils/formatPrintValue';
import { formatTypedPrintValue } from '../utils/typedValueFormat';
import {
  findDatasetItem,
  resolvePrintColumns,
} from '../utils/resolvePrintColumns';

export interface ReportBlocksContext {
  /** Master definition items; the fallback when a source has no definition. */
  items: DatasetItem[];
  /** Definition items per source, so subset columns resolve against their own. */
  itemsBySource?: Record<string, DatasetItem[]>;
  rowsBySource: ReportRowsBySource;
  defaultSource: ReportDataSource;
  datasetLabel: string;
  activeFilters: Record<string, unknown> | null;
  /** Actual filters applied to subordinate row sources. */
  activeFiltersBySource?: Record<string, Record<string, unknown>>;
  printedAt: Date;
  /** Row count shown by the rowCount meta block — the items section's row count. */
  itemRowCount: number;
  /** Authenticated blob URLs keyed by persistent report asset UUID. */
  assetUrls?: Record<string, string>;
}

interface ReportBlocksProps extends ReportBlocksContext {
  blocks: ReportBlock[];
}

/**
 * A block label may be free text the user typed in the designer, or one of the
 * built-in print.* keys the generator emits. t() returns the id unchanged when
 * no catalog entry matches, so both work through the same call.
 */
const translateLabel = (t: TranslateFn, label: string): string => t(label);

const ReportBlocks = ({ blocks, ...context }: ReportBlocksProps) => {
  const { t, language } = useTranslate();
  const {
    items,
    itemsBySource,
    rowsBySource,
    defaultSource,
    datasetLabel,
    activeFilters,
    activeFiltersBySource,
    printedAt,
    itemRowCount,
    assetUrls,
  } = context;

  /**
   * Definition items of a source. Falling back to the master items keeps a
   * template working while a subset definition is still loading, rather than
   * printing bare item names.
   */
  const itemsForSource = (source: string): DatasetItem[] =>
    itemsBySource?.[source] ?? items;

  const renderMeta = (
    field: string,
    index: number,
    label?: string,
    style?: ReportTextStyle,
    filterItem?: string,
    filterSource?: ReportDataSource,
    align?: ReportAlignment
  ) => {
    const alignmentClass = `report-align-${align ?? 'left'}`;
    // 'reportTitle' is what the designer offers; 'datasetLabel' is the older
    // name for the same value, kept so existing templates keep working.
    if (field === 'reportTitle' || field === 'datasetLabel') {
      return (
        <p
          className={`report-title ${alignmentClass}`}
          key={index}
          style={resolveTextStyle(style)}
        >
          {datasetLabel}
        </p>
      );
    }

    if (field === 'filterValue') {
      // One named filter, for a header that states which warehouse or branch the
      // report covers. Absent filter prints nothing rather than a stray label.
      const filter = findFilterValue(
        describeActiveFilters(
          activeFilters,
          items,
          t,
          language,
          itemsBySource,
          activeFiltersBySource
        ),
        filterItem,
        filterSource
      );
      if (!filter) {
        return null;
      }

      return (
        <p
          className={`report-meta ${alignmentClass}`}
          key={index}
          style={resolveTextStyle(style)}
        >
          <span className="report-meta-label">
            {label ? translateLabel(t, label) : filter.label}
          </span>
          <span>{filter.value}</span>
        </p>
      );
    }

    if (field === 'printedAt') {
      return (
        <p
          className={`report-meta ${alignmentClass}`}
          key={index}
          style={resolveTextStyle(style)}
        >
          <span className="report-meta-label">
            {label ? translateLabel(t, label) : t('print.printedAt')}
          </span>
          <span>
            {formatTypedPrintValue(
              printedAt,
              'dateTime',
              '@septim/dateTime:short',
              language
            ) ?? printedAt.toLocaleString()}
          </span>
        </p>
      );
    }

    if (field === 'rowCount') {
      return (
        <div
          className={`report-summary-line ${alignmentClass}`}
          key={index}
          style={resolveTextStyle(style)}
        >
          <span className="report-summary-label">
            {label ? translateLabel(t, label) : t('print.rowCount')}
          </span>
          <span className="report-summary-value">{itemRowCount}</span>
        </div>
      );
    }

    if (field === 'filters') {
      const filters = describeActiveFilters(
        activeFilters,
        items,
        t,
        language,
        itemsBySource,
        activeFiltersBySource
      );

      return (
        <p
          className={`report-meta ${alignmentClass}`}
          key={index}
          style={resolveTextStyle(style)}
        >
          <span className="report-meta-label">
            {label ? translateLabel(t, label) : t('print.filters')}
          </span>
          <span>
            {filters.length === 0
              ? t('print.noFilters')
              : // In the words the filter UI uses, so the report reads as a
                // sentence: "Sklad Praha; Datum větší nebo rovno 1.8.2026".
                filters.map(formatFilterDescription).join('; ')}
          </span>
        </p>
      );
    }

    return null;
  };

  const renderBlock = (block: ReportBlock, index: number) => {
    switch (block.kind) {
      case 'text':
        return (
          <p
            key={index}
            className={`report-text report-align-${block.align ?? 'left'}`}
            style={resolveTextStyle(block.style)}
          >
            {block.text}
          </p>
        );

      case 'logo':
        return (
          <img
            key={index}
            className={`report-logo report-align-${block.align ?? 'left'}`}
            src={
              block.assetId
                ? assetUrls?.[block.assetId]
                : (block.src ?? DEFAULT_LOGO_SRC)
            }
            alt=""
            style={{
              height: `${block.heightMm ?? DEFAULT_LOGO_HEIGHT_MM}mm`,
            }}
          />
        );

      case 'meta':
        return renderMeta(
          block.field,
          index,
          block.label,
          block.style,
          block.filterItem,
          block.filterSource,
          block.align
        );

      case 'fields': {
        const blockSource = block.source ?? defaultSource;

        return (
          <div
            key={index}
            className="report-fields"
            style={{
              gridTemplateColumns: `repeat(${block.columns ?? 1}, minmax(0, 1fr))`,
            }}
          >
            {block.fields.map((field, fieldIndex) => {
              // Each field may read from its own dataset, so a header can mix
              // fields from several datasets of the same screen.
              const source = field.source ?? blockSource;
              // Field blocks describe a single record; the first row of the
              // source is that record.
              const row = (rowsBySource[source] ?? [])[0];
              const [column] = resolvePrintColumns(
                [
                  {
                    item: field.item,
                    label: field.label,
                    format: field.format,
                  },
                ],
                itemsForSource(source)
              );

              return (
                <div
                  className="report-field"
                  key={`${source}:${field.item}:${fieldIndex}`}
                  style={resolveTextStyle(field.style)}
                >
                  <span className="report-field-label">{column.label}</span>
                  <span className="report-field-value">
                    {row ? formatPrintValue(row, column, language) : ''}
                  </span>
                </div>
              );
            })}
          </div>
        );
      }

      case 'aggregate': {
        const rows = rowsBySource[block.source] ?? [];
        const value = computeAggregate(rows, block.operation, block.item);
        const definitionItem = block.item
          ? findDatasetItem(itemsForSource(block.source), block.item)
          : undefined;
        // A format on the block is an explicit override of the definition's.
        const formatItem = block.format
          ? {
              ...definitionItem,
              name: block.item ?? 'value',
              format: block.format,
            }
          : definitionItem;
        const formatted =
          block.operation === 'count'
            ? String(value ?? 0)
            : formatPrintNumber(value, formatItem);

        return (
          <div
            className="report-summary-line"
            key={index}
            style={resolveTextStyle(block.style)}
          >
            <span className="report-summary-label">
              {translateLabel(t, block.label)}
            </span>
            <span className="report-summary-value">{formatted}</span>
          </div>
        );
      }

      case 'taxBreakdown': {
        const rows = rowsBySource[block.source] ?? [];
        const { rows: breakdownRows, total } = computeTaxBreakdown(rows, block);
        const sourceItems = itemsForSource(block.source);
        const rateItem = findDatasetItem(sourceItems, block.groupBy);
        const netItem = findDatasetItem(sourceItems, block.net);
        const grossItem = findDatasetItem(sourceItems, block.gross);

        if (breakdownRows.length === 0) {
          return null;
        }

        return (
          <div
            className="report-tax-breakdown"
            key={index}
            style={resolveTextStyle(block.style)}
          >
            <span className="report-summary-label">
              {translateLabel(t, block.label)}
            </span>
            <table className="report-tax-table">
              <thead>
                <tr>
                  <th scope="col">{t('print.taxRate')}</th>
                  <th scope="col">{t('print.taxBase')}</th>
                  <th scope="col">{t('print.taxAmount')}</th>
                  <th scope="col">{t('print.taxTotal')}</th>
                </tr>
              </thead>
              <tbody>
                {breakdownRows.map((breakdownRow) => (
                  <tr key={String(breakdownRow.rate)}>
                    <td>
                      {breakdownRow.rate === null
                        ? ''
                        : formatPrintNumber(breakdownRow.rate, rateItem)}
                    </td>
                    <td>{formatPrintNumber(breakdownRow.net, netItem)}</td>
                    {/* Tax shares the base column's money format. */}
                    <td>{formatPrintNumber(breakdownRow.tax, netItem)}</td>
                    <td>{formatPrintNumber(breakdownRow.gross, grossItem)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{t('print.total')}</td>
                  <td>{formatPrintNumber(total.net, netItem)}</td>
                  <td>{formatPrintNumber(total.tax, netItem)}</td>
                  <td>{formatPrintNumber(total.gross, grossItem)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      }

      case 'row': {
        // Snapped again at render time: the renderer is the authority on what is
        // printable, so a stored layout can never reach paper in a state it
        // cannot lay out.
        const cells = snapGridRow(block.cells);

        return (
          <div
            className="report-row"
            key={index}
            style={{
              gridTemplateColumns: `repeat(${REPORT_GRID_COLUMNS}, minmax(0, 1fr))`,
            }}
          >
            {cells.map((cell, cellIndex) => (
              <div
                className={`report-row-cell report-row-cell--${cell.align ?? 'left'}`}
                key={cellIndex}
                style={{ gridColumn: `span ${cell.span ?? 1}` }}
              >
                {cell.blocks.map((cellBlock, blockIndex) =>
                  renderBlock(cellBlock, blockIndex)
                )}
              </div>
            ))}
          </div>
        );
      }

      case 'line':
        return <hr className="report-line" key={index} />;

      case 'spacer':
        return (
          <div
            className="report-spacer"
            key={index}
            style={{ height: `${block.heightMm ?? 4}mm` }}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {blocks.map((block, index) => (
        <Fragment key={index}>{renderBlock(block, index)}</Fragment>
      ))}
    </>
  );
};

export default ReportBlocks;
